import type { CellContext } from '@tanstack/react-table';
import * as React from 'react';
import { Row, Col } from 'react-bootstrap';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import type { FieldArrayWithId, Path, UseFormClearErrors, UseFormGetValues, UseFormSetError } from 'react-hook-form';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';
import { compose } from 'redux';

import type { W_FieldReport_FindAllReportTypesForCompany_VM } from 'ab-api/web/fieldReport/findAllReportTypesForCompany';
import type { W_JobTracking_FindJobsForTemplate_VM } from 'ab-api/web/jobTracking/findJobsForTemplate';

import { filterMap } from '@acceligentllc/shared/utils/array';

import { JobTrackingTemplateColumnType, JobTrackingTemplateColumnTypeLabel } from '@acceligentllc/shared/enums/jobTrackingTemplateColumnType';

import { Button, SegmentLabel } from '@acceligentllc/storybook';

import ConfirmationModal from 'af-components/ConfirmationModal';
import SimpleTable from 'af-components/Controls/SimpleTable';
import type { SimpleTableProps as BasicTableProps } from 'af-components/Controls/SimpleTable/types';
import CheckboxField from 'af-components/RHFFieldWrappers/Checkbox/CheckboxField';
import InputField from 'af-components/RHFFieldWrappers/Input/InputField';
import type { SimpleTableRow } from 'af-components/RHFFieldWrappers/SimpleTableRHFWrapper';
import SimpleTableRHFWrapper from 'af-components/RHFFieldWrappers/SimpleTableRHFWrapper';
import type { InputCellType } from 'af-components/RHFTableCellComponents/Input/types';
import type { TableRef } from 'af-components/RHFTableCellComponents/SimpleTable';
import type { FooterButton, SimpleTableProps } from 'af-components/RHFTableCellComponents/SimpleTable/types';
import DropdownCellRenderer from 'af-components/RHFTableCellRenderers/DropdownCellRenderer';
import InputCellRenderer from 'af-components/RHFTableCellRenderers/InputCellRenderer';
import TextCell from 'af-components/Table/Cells/TextCell';
import Tooltip from 'af-components/Tooltip';

import * as FieldReportActions from 'af-actions/fieldReport';
import * as JobTrackingActions from 'af-actions/jobTracking';

import type FormModel from './formModel';
import type { ColumnDataSourceFM, TemplateColumnFM } from './formModel';
import { groupDataSourcesByReportType, indexReportTypes, resolveFieldName, withCidColumn, withCidDS } from './helpers';
import styles from './styles.module.scss';
import AddFieldsToColumnsModal from '../AddFieldsToColumnsModal';
import ReorderColumnsModal from '../ReorderColumnsModal';

interface OwnProps {
	onSubmit: (form: FormModel) => Promise<void>;
	initial?: Partial<FormModel>;
	isEdit: boolean;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

export type TemplateColumnKey = keyof TemplateColumnFM;

export type ColumnsFieldPath = `columns.${number}.${TemplateColumnKey}`;

type TableRow = Omit<TemplateColumnFM, 'id'> & SimpleTableRow;

interface PreviewRow {
	[key: string]: string | number;
}

const columnFieldKeys: Array<keyof TemplateColumnFM> = [
	'name',
	'type',
	'index',
	'isDefinition',
	'excludedFieldReportIds',
	'dataSources',
];

function isColumnField(field: string): field is keyof TemplateColumnFM {
	return columnFieldKeys.includes(field as keyof TemplateColumnFM);
}

const requiredFields: Array<keyof TemplateColumnFM> = ['name', 'type', 'index'];
const uniqueFields: Array<keyof TemplateColumnFM> = ['name', 'index'];
const integerFields: Array<keyof TemplateColumnFM> = ['index'];

type MappableTemplateColumnKeys = Exclude<
	keyof TemplateColumnFM,
	'_cid' | 'id'
>;

const fieldNameMapper: Record<MappableTemplateColumnKeys, string> = {
	name: 'Column Name',
	type: 'Column Type',
	index: 'Position',
	isDefinition: 'Definition',
	excludedFieldReportIds: 'Excluded Reports',
	dataSources: 'Data from Fields',
};

function validateOneColumnField(
	rowPath: Path<FormModel>,
	fieldName: keyof TemplateColumnFM,
	value: TemplateColumnFM[keyof TemplateColumnFM],
	getValuesFn: UseFormGetValues<FormModel>
): Nullable<string> {
	const isValueEmpty =
		value === null ||
		value === undefined ||
		value === '';

	if (requiredFields.includes(fieldName) && isValueEmpty) {
		return `${fieldNameMapper[fieldName]} is required`;
	}

	if (integerFields.includes(fieldName)) {
		if (!isValueEmpty) {
			const number = +value;
			if (!Number.isInteger(number) || number < 0) {
				return `${fieldNameMapper[fieldName]} must be a non-negative integer`;
			}
		}
	}

	// type-specific rules involving this field
	if (fieldName === 'dataSources') {
		const row = getValuesFn(rowPath) as TemplateColumnFM;
		const type = row?.type;
		const dataSources = row?.dataSources ?? [];
		const hasField = dataSources.some((d) => !!d?.field);

		if (type === JobTrackingTemplateColumnType.CUSTOM) {
			if (!hasField) {
				return 'At least one data source is required for Custom columns';
			}
		} else {
			if (hasField) {
				return 'Data sources are not allowed for non-Custom columns';
			}
		}
	}

	// date-specific field checks (we also double-check in array validator)
	if (fieldName === 'isDefinition' || fieldName === 'index' || fieldName === 'type') {
		const row = getValuesFn(rowPath) as TemplateColumnFM;
		const type = fieldName === 'type' ? value : row?.type;
		const isDef = fieldName === 'isDefinition' ? value : row?.isDefinition;
		if (type === JobTrackingTemplateColumnType.DATE && !isDef) {
			return 'Date column must have Definition = true';
		}
	}

	return null;
}

// whole-array validation (uniqueness + cross-row rules)
function validateColumnsArray(
	values: FormModel,
	setErr: UseFormSetError<FormModel>,
	clearErr: UseFormClearErrors<FormModel>,
	getValues: UseFormGetValues<FormModel>
): boolean {
	// reset errors
	clearErr('columns');

	const cols = values.columns ?? [];
	let hasErrors = false;

	// per-row base checks (required/int/type-specific/date rule)
	const keysToValidate = columnFieldKeys.filter(
		(fieldName) => fieldName !== 'excludedFieldReportIds'
	);

	cols.forEach((column, index) => {
		for (const fieldName of keysToValidate) {
			const msg = validateOneColumnField(`columns.${index}`, fieldName, column[fieldName], getValues);
			if (!msg) {
				continue;
			}

			hasErrors = true;
			setErr(`columns.${index}.${fieldName}`, {
				message: msg,
			});
		}
	});

	// uniqueness maps
	const { byName, byIndex } = cols.reduce<{
		byName: Map<string, number[]>;
		byIndex: Map<number, number[]>;
	}>(
		(acc, column, index) => {
			if (column.name) {
				const key = String(column.name).toLowerCase();
				const arr = acc.byName.get(key) ?? [];
				acc.byName.set(key, [...arr, index]);
			}

			if (column.index !== undefined && column.index !== null && Number.isInteger(+column.index)) {
				const key = +column.index;
				const arr = acc.byIndex.get(key) ?? [];
				acc.byIndex.set(key, [...arr, index]);
			}

			return acc;
		},
		{
			byName: new Map(),
			byIndex: new Map(),
		}
	);

	// duplicates => errors on each duplicate
	const checkMap = (
		map: Map<string | number, number[]>,
		fieldName: 'index' | 'name',
		message: string
	) => {
		for (const columnIndexes of map.values()) {
			if (columnIndexes.length <= 1) {
				continue;
			}
			hasErrors = true;
			for (const index of columnIndexes) {
				setErr(`columns.${index}.${fieldName}`, {
					message,
				});
			}
		}
	};

	checkMap(byName, 'name', 'Column Name must be unique');
	checkMap(byIndex, 'index', 'Position must be unique');

	// date-specific cross-row rule: any date must be definition
	cols.forEach((column, index) => {
		if (column.type === JobTrackingTemplateColumnType.DATE && !column.isDefinition) {
			hasErrors = true;
			setErr(`columns.${index}.isDefinition`, {
				message: 'Date column must have be a definition column',
			});
		}
	});

	// need at least one date column
	const hasDate = cols.some((c) => c.type === JobTrackingTemplateColumnType.DATE);
	if (!hasDate && cols.length > 0) {
		hasErrors = true;
		setErr('columns', {
			message: 'At least one Date column is required',
		});
	}

	return hasErrors;
}

function nextAvailableIndex(cols: TemplateColumnFM[]): number {
	return cols.length + 1;
}

type TypeOption = {
	label: string;
	value: JobTrackingTemplateColumnType;
	id: JobTrackingTemplateColumnType;
};

const typeOptions: TypeOption[] = [
	{ label: 'Preset - Date', value: JobTrackingTemplateColumnType.DATE, id: JobTrackingTemplateColumnType.DATE },
	{ label: 'Default - Work Order', value: JobTrackingTemplateColumnType.WORK_ORDER_ID, id: JobTrackingTemplateColumnType.WORK_ORDER_ID },
	{ label: 'Default - Crew Type', value: JobTrackingTemplateColumnType.CREW_TYPE, id: JobTrackingTemplateColumnType.CREW_TYPE },
	{ label: 'Custom', value: JobTrackingTemplateColumnType.CUSTOM, id: JobTrackingTemplateColumnType.CUSTOM },
];

const PRESET_TYPES = new Set<TemplateColumnFM['type']>([JobTrackingTemplateColumnType.DATE, JobTrackingTemplateColumnType.WORK_ORDER_ID, JobTrackingTemplateColumnType.CREW_TYPE]);

const defaultNameForType = (type: TemplateColumnFM['type']) => {
	if (PRESET_TYPES.has(type)) {
		return JobTrackingTemplateColumnTypeLabel[type];
	}
	return '';
};

function hasAnotherOfType(
	cols: TemplateColumnFM[],
	t: TemplateColumnFM['type'],
	exceptRowIdx: number
) {
	if (!PRESET_TYPES.has(t)) {
		return false;
	}
	return cols.some((c, i) => i !== exceptRowIdx && c.type === t);
}

const JobTrackingForm: React.FC<Props> = (props: Props) => {

	const {
		onSubmit,
		initial,
		getCompanyFieldReportTypes,
		getJobsForTemplate,
		isEdit,
	} = props;

	const tableRef = React.useRef<TableRef>(null);

	const [companyFieldReportTypes, setCompanyFieldReportTypes] = React.useState<Nullable<W_FieldReport_FindAllReportTypesForCompany_VM>>(null);
	const [showModal, setShowModal] = React.useState<boolean>(false);
	const [modalRowIndex, setModalRowIndex] = React.useState<Nullable<number>>(null);
	const [modalInitial, setModalInitial] = React.useState<Nullable<TemplateColumnFM>>(null);
	const [modalBaseline, setModalBaseline] = React.useState<Nullable<TemplateColumnFM>>(null);
	const [showReorder, setShowReorder] = React.useState(false);
	const [warningModalOpen, setWarningModalOpen] = React.useState(false);
	const [jobsForTemplate, setJobsForTemplate] = React.useState<Nullable<W_JobTracking_FindJobsForTemplate_VM>>(null);
	const pendingFormRef = React.useRef<Nullable<FormModel>>(null);

	const closeWarningModal = React.useCallback(() => {
		setWarningModalOpen(false);
		pendingFormRef.current = null;
	}, []);

	const confirmWarningModal = React.useCallback(async () => {
		const pendingForm = pendingFormRef.current;

		if (!pendingForm) {
			return;
		}

		await onSubmit(pendingForm);

		pendingFormRef.current = null;
		setWarningModalOpen(false);
	}, [onSubmit]);

	const navigate = useNavigate();

	const initialColumns = React.useMemo<TemplateColumnFM[]>(() => {
		const raw = (initial?.columns ?? []).map((column) =>
			withCidColumn({
				...column,
				dataSources: (column.dataSources ?? []).map((ds) => withCidDS(ds)),
			})
		);

		// EDIT: never auto-inject Date column when editing an existing template
		if (isEdit) {
			return raw;
		}

		const hasDate = raw.some((c) => c.type === JobTrackingTemplateColumnType.DATE);
		if (hasDate) {
			return raw;
		}

		// inject Date column if missing (CREATE only)
		const dateCol = withCidColumn({
			id: undefined,
			name: JobTrackingTemplateColumnTypeLabel[JobTrackingTemplateColumnType.DATE],
			type: JobTrackingTemplateColumnType.DATE,
			index: 1,
			isDefinition: true,
			excludedFieldReportIds: [],
			dataSources: [],
		});

		return [dateCol, ...raw];
	}, [initial?.columns, isEdit]);

	const {
		control,
		unregister,
		handleSubmit,
		formState,
		reset,
		getValues,
		setError,
		clearErrors,
		setValue,
		trigger,
	} = useForm<FormModel>({
		defaultValues: {
			id: initial?.id,
			name: initial?.name ?? '',
			description: (initial?.description ?? ''),
			columns: initial?.columns ?? initialColumns,
		},
	});

	const templateName = useWatch({ control, name: 'name' });

	const {
		fields,
		append,
		remove,
		update,
		replace,
	} = useFieldArray<FormModel, 'columns', '_cid'>({
		control,
		name: 'columns',
		keyName: '_cid',
	});

	const fetchReportTypeData = React.useCallback(async () => {
		const data = await getCompanyFieldReportTypes();
		setCompanyFieldReportTypes(data);
	}, [getCompanyFieldReportTypes]);

	const fetchAllJobsForTemplate = React.useCallback(async () => {
		const templateId = initial?.id;
		if (!templateId) {
			return;
		}

		const jobs = await getJobsForTemplate(templateId);
		setJobsForTemplate(jobs);
	}, [getJobsForTemplate, initial]);

	React.useEffect(() => {
		fetchReportTypeData();
	}, [, fetchReportTypeData]);

	React.useEffect(() => {
		fetchAllJobsForTemplate();
	}, [fetchAllJobsForTemplate]);

	React.useEffect(() => {
		reset({
			id: initial?.id,
			name: initial?.name ?? '',
			description: initial?.description ?? '',
			columns: initialColumns,
		});
	}, [initial?.id, initial?.name, initial?.description, initialColumns, reset]);

	const familyMeta = React.useMemo(() => {
		const map = (companyFieldReportTypes?.reportTypes ?? []).reduce<
			Map<string, { latestId: number; latestRev: number; allIds: number[]; }>
		>((_map, rt) => {
			const familyKey = rt.familyKey;
			const node = _map.get(familyKey);

			if (!node) {
				_map.set(familyKey, {
					latestId: rt.id,
					latestRev: rt.revision,
					allIds: [rt.id],
				});
				return _map;
			}

			node.allIds.push(rt.id);

			if (rt.revision > node.latestRev) {
				node.latestId = rt.id;
				node.latestRev = rt.revision;
			}

			return _map;
		}, new Map());

		return map;
	}, [companyFieldReportTypes]);

	const getDoNotUseFamiliesForExcluded = React.useCallback(
		(excludedIds: Nullable<number[]>) => {
			const excluded = excludedIds ?? [];

			const res = Array.from(familyMeta.entries()).reduce<string[]>((acc, [famKey, meta]) => {
				const latestExcluded = excluded.includes(meta.latestId);
				const allExcluded = meta.allIds.length > 0 && meta.allIds.every((id) => excluded.includes(id));

				if (latestExcluded || allExcluded) { // we count as excluded if last one is excluded or all are
					acc.push(famKey);
				}

				return acc;
			}, []);

			return res;
		},
		[familyMeta]
	);

	const hasAnyErrors = (!!formState.errors && Object.keys(formState.errors).length > 0) || !formState.isValid; // wrapping this in react memo causes problems - it does not refresh when it should

	const isColumnOutdated = React.useCallback(
		(col: TemplateColumnFM): boolean => {
			if (!companyFieldReportTypes) {
				return false;
			}

			const groupedDataSources = groupDataSourcesByReportType(
				col.dataSources ?? [],
				companyFieldReportTypes,
				col.excludedFieldReportIds ?? []
			);

			const doNotUseFamilies = getDoNotUseFamiliesForExcluded(
				col.excludedFieldReportIds ?? []
			);

			// Column is outdated if there exists a report type family which:
			// - has at least one data source in this column
			// - is NOT fully excluded (doNotUse)
			// - does NOT have all revisions covered
			for (const groupedDataSource of groupedDataSources) {
				if (!groupedDataSource.hasAnyDataSource || doNotUseFamilies.includes(groupedDataSource.familyKey)) {
					continue;
				}
				if (!groupedDataSource.allRevisionsCovered) {
					return true;
				}
			}

			return false;
		},
		[companyFieldReportTypes, getDoNotUseFamiliesForExcluded]
	);

	const onSubmitRHF = React.useCallback(async () => {
		const raw = getValues();

		const normalized: FormModel = {
			...raw,
			columns: raw.columns ?? [],
			description: raw.description?.trim() ? raw.description : null,
		};

		// validate columns just to make sure it's all good
		const hasColumnErrors = validateColumnsArray(
			normalized,
			setError,
			clearErrors,
			getValues
		);

		// Make sure there are no outdated ones
		let hasOutdated = false;
		const cols = normalized.columns ?? [];

		cols.forEach((c, idx) => {
			if (isColumnOutdated(c)) {
				hasOutdated = true;
				setError(`columns.${idx}.index` as Path<FormModel>, {
					message:
						'This column uses outdated fields. Please review the field mapping or exclude outdated revisions.',
				});
			}
		});

		// If there are any errors, don't submit
		if (hasColumnErrors || hasOutdated) {
			return;
		}

		if (isEdit && jobsForTemplate?.jobs?.length) {
			pendingFormRef.current = normalized;
			setWarningModalOpen(true);
			return;
		}

		await onSubmit(normalized);
	}, [getValues, setError, clearErrors, isEdit, jobsForTemplate?.jobs?.length, onSubmit, isColumnOutdated]);

	// For tooltip: which dataSources in this column are considered outdated
	const getOutdatedDataSourcesForColumn = React.useCallback(
		(dataSources: ColumnDataSourceFM[], excludedIds: number[]) => {
			if (!dataSources?.length || !companyFieldReportTypes) {
				return [] as ColumnDataSourceFM[];
			}

			const groupedDataSources = groupDataSourcesByReportType(
				dataSources,
				companyFieldReportTypes,
				excludedIds
			);

			const doNotUseFamilies = getDoNotUseFamiliesForExcluded(excludedIds);

			const result: ColumnDataSourceFM[] = [];

			for (const groupedDataSource of groupedDataSources) {
				if (
					!groupedDataSource.hasAnyDataSource ||
					doNotUseFamilies.includes(groupedDataSource.familyKey) ||
					groupedDataSource.allRevisionsCovered
				) {
					continue;
				}

				const revisions = groupedDataSource.revisions;

				// assume revisions[] is ordered by revision asc -> walk backwards to get latest
				let latestWithDataSource: Nullable<ColumnDataSourceFM> = null;

				for (let i = revisions.length - 1; i >= 0; i--) {
					const rev = revisions[i];
					if (rev.hasDataSource && rev.dataSource) {
						latestWithDataSource = rev.dataSource;
						break;
					}
				}

				if (latestWithDataSource) {
					result.push(latestWithDataSource);
				}
			}

			return result;
		},
		[companyFieldReportTypes, getDoNotUseFamiliesForExcluded]
	);

	const getLatestAssociatedDataSourcesByFamily = React.useCallback(
		(dataSources: ColumnDataSourceFM[], excludedIds: number[]) => {
			if (!companyFieldReportTypes || !dataSources?.length) {
				return [] as ColumnDataSourceFM[];
			}

			const excluded = excludedIds ?? [];
			const { byFamily: reportTypesByFamily } = indexReportTypes(companyFieldReportTypes);

			const dsByRtId = dataSources.reduce<Map<number, ColumnDataSourceFM>>((acc, ds) => {
				const rtId = ds.field?.reportTypeId;

				if (rtId && !excluded.includes(rtId) && !acc.has(rtId)) {
					acc.set(rtId, ds);
				}

				return acc;
			}, new Map());

			return Array.from(reportTypesByFamily.values()).reduce<ColumnDataSourceFM[]>((acc, revisions) => {
				if (!revisions.length) {
					return acc;
				}

				for (let i = 0; i < revisions.length; i++) {
					const revision = revisions[i];
					const ds = dsByRtId.get(revision.id);

					if (ds) {
						acc.push(ds);
						break;
					}
				}

				return acc;
			}, []);
		},
		[companyFieldReportTypes]
	);

	const renderIndexCell = React.useCallback(
		(cell: CellContext<TemplateColumnFM & SimpleTableRow, number>) => {
			const column = cell.row.original;

			const outdated = isColumnOutdated(column);

			let tooltipMessage = <></>;

			if (outdated) {
				const outdatedDataSources = getOutdatedDataSourcesForColumn(
					column.dataSources,
					column.excludedFieldReportIds
				);

				const outdatedDataSourcesNamesMeta = outdatedDataSources.map((ds) =>
					resolveFieldName(companyFieldReportTypes, ds)
				);

				const names = filterMap(
					outdatedDataSourcesNamesMeta,
					(nameMeta) => !!nameMeta,
					(nameMeta) =>
						`${nameMeta!.reportTypeName} - ${nameMeta!.reportBlockName} - ${nameMeta!.reportBlockFieldName}`
				);

				tooltipMessage = (
					<>
						Some changes have been made
						<br />
						to the fields used to populate the job tracking table.
						<br />
						Affected fields:
						<br />
						{names.map((name) => (
							<b key={name}>
								{name}
								<br />
							</b>
						))}
					</>
				);
			}

			return (
				<div className={styles['index-cell']}>
					<TextCell value={cell.row.index + 1} />
					{outdated && (
						<Tooltip message={tooltipMessage}>
							<span className={`icon-warning ${styles['index-cell__warning']}`} />
						</Tooltip>
					)}
				</div>
			);
		},
		[
			isColumnOutdated,
			getOutdatedDataSourcesForColumn,
			companyFieldReportTypes,
		]
	);

	const addNewColumn = React.useCallback(async () => {
		const cols = getValues('columns') ?? [];
		const idx = nextAvailableIndex(cols);

		const newCol: TemplateColumnFM = withCidColumn({
			id: undefined,
			name: '',
			type: JobTrackingTemplateColumnType.CUSTOM,
			index: idx,
			isDefinition: false,
			excludedFieldReportIds: [],
			dataSources: [],
		});

		append(newCol);

		const snapshot = getValues();
		validateColumnsArray(snapshot, setError, clearErrors, getValues);
	}, [append, getValues, setError, clearErrors]);

	const watchColumns = useWatch({ control, name: 'columns' });

	const hasOutdatedColumns = React.useMemo(
		() =>
			(watchColumns ?? []).some(
				(col) => col && isColumnOutdated(col as TemplateColumnFM)
			),
		[watchColumns, isColumnOutdated]
	);

	const guardedRemove = React.useCallback((index: number) => {
		const columns = getValues('columns') ?? [];
		const column = columns[index];

		if (!column) {
			return;
		}

		// Hard-block removal of Date column
		if (column.type === JobTrackingTemplateColumnType.DATE) {
			return;
		}

		remove(index);

		// revalidate columns after removal
		const snapshot = getValues();
		validateColumnsArray(snapshot, setError, clearErrors, getValues);
	}, [getValues, remove, setError, clearErrors]);

	const footerButtonsLeft: FooterButton[] = React.useMemo(() => {
		const resolvedFooterButtons = [{
			iconName: 'ActionPlusSmall',
			label: 'Add New Column',
			onClick: addNewColumn,
		}];
		return resolvedFooterButtons;
	}, [addNewColumn]);

	const closeModal = React.useCallback(() => {
		setShowModal(false);
		setModalRowIndex(null);
	}, []);

	const openModal = React.useCallback((rowIndex: number) => () => {
		setModalRowIndex(rowIndex);

		const column = getValues(`columns.${rowIndex}`);
		// ensure data source rows have _cid
		column.dataSources = column.dataSources?.map((d) => d._cid ? d : withCidDS(d)) ?? [];

		setModalInitial(column);

		// We want a baseline column state in case of edits, so we can revert to that in case of "Do not use but keep data" option
		let baseline: Nullable<TemplateColumnFM> = null;
		if (props.isEdit) {
			const initCols = (initial?.columns ?? []) as TemplateColumnFM[];
			const draftId = column.id;
			baseline = initCols.find((c) => c.id === draftId) ?? null;
		}
		setModalBaseline(baseline);
		setShowModal(true);
	}, [getValues, initial?.columns, props.isEdit]);

	const saveColumnFieldChanges = React.useCallback(
		(column: TemplateColumnFM) => {
			if (modalRowIndex === null) {
				return;
			}

			const currentRow = getValues(`columns.${modalRowIndex}`) as Nullable<TemplateColumnFM>;

			if (!currentRow) {
				return;
			}

			// override current state with new changes
			const merged: TemplateColumnFM = {
				...currentRow,
				...column,
				_cid: currentRow._cid ?? column._cid,
			};

			update(modalRowIndex, merged);

			// revalidate columns after update
			const snapshot = getValues();
			validateColumnsArray(snapshot, setError, clearErrors, getValues);

			closeModal();
		},
		[modalRowIndex, update, getValues, setError, clearErrors, closeModal]
	);

	const validateOnChange = React.useCallback(
		(cellName: ColumnsFieldPath, newValue?: TemplateColumnFM[keyof TemplateColumnFM]) => {
			const parts = cellName.split('.');
			if (parts.length !== 3 || parts[0] !== 'columns') {
				return; // something is wrong, should not happen
			}

			const rowIdx = +parts[1];
			const fieldName = parts[2];
			if (!Number.isInteger(rowIdx) || !isColumnField(fieldName)) {
				return;
			}

			clearErrors(cellName);
			const value = newValue ?? getValues(cellName); // prefer newValue if given

			// --- type-specific logic ---
			if (fieldName === 'type') {
				const newType = value as TemplateColumnFM['type'];
				const columns = getValues('columns') ?? [];

				// Only one per preset
				if (PRESET_TYPES.has(newType) && hasAnotherOfType(columns, newType, rowIdx)) {
					setError(cellName, {
						message: `Only one "${newType}" column is allowed.`,
					});
					return;
				}

				const dataSourcePath = `columns.${rowIdx}.dataSources` as Path<FormModel>;
				const isDefinitionPath = `columns.${rowIdx}.isDefinition` as Path<FormModel>;
				const namePath = `columns.${rowIdx}.name` as Path<FormModel>;

				// dataSources autofill/clear
				if (newType === JobTrackingTemplateColumnType.CUSTOM) {
					setValue(dataSourcePath, [], { shouldDirty: true, shouldValidate: false });
				} else {
					setValue(dataSourcePath, [], { shouldDirty: true, shouldValidate: false });
					if (newType === JobTrackingTemplateColumnType.DATE) { // make isDefinition for date type column
						setValue(isDefinitionPath, true, { shouldDirty: true, shouldValidate: false });
					}
				}

				// name autofill
				const nextDefaultName = defaultNameForType(newType);

				setValue(namePath, nextDefaultName, { shouldDirty: true, shouldValidate: false });

				// sync the field-array snapshot so the table updates
				const snap = getValues(`columns.${rowIdx}`) as TemplateColumnFM;
				const currentFAItem = fields[rowIdx];
				update(rowIdx, { ...snap, _cid: currentFAItem._cid ?? snap._cid });
			}

			// --- single-field validation ---
			const msg = validateOneColumnField(`columns.${rowIdx}`, fieldName, value, getValues);
			if (msg) {
				setError(cellName as Path<FormModel>, { message: msg });
				return;
			}

			// --- cross-row checks ---
			if (
				uniqueFields.includes(fieldName) ||
				fieldName === 'type' ||
				fieldName === 'dataSources' ||
				fieldName === 'isDefinition'
			) {
				const snapshot = getValues();
				validateColumnsArray(snapshot, setError, clearErrors, getValues);
			}
		},
		[clearErrors, getValues, fields, update, setError, setValue]
	);

	const getRowRHFId = React.useCallback((rowIndex: number) => {
		const field = fields[rowIndex];
		return field?._cid ?? `${rowIndex}`;
	}, [fields]);

	const renderInputCell = React.useCallback((
		cellName: TemplateColumnKey, cellType: InputCellType
	) => (cell: CellContext<TableRow, string>) => {
		const rowIdx = cell.row.index;
		const rowId = getRowRHFId(rowIdx);

		return (
			<InputCellRenderer
				className={styles['input-cell']}
				control={control}
				fieldsLength={fields.length}
				getValues={getValues}
				name={`columns.${cell.row.index}.${cellName}`}
				onChange={validateOnChange}
				rhfId={rowId}
				type={cellType}
				unregister={unregister}
			/>
		);
	}, [getRowRHFId, control, fields.length, getValues, validateOnChange, unregister]);

	const handleDropdownChange = React.useCallback((rowIndex: number) => () => {
		const val = getValues(`columns.${rowIndex}.type` as Path<FormModel>) as TemplateColumnFM['type'];
		if (!val) {
			return;
		}
		validateOnChange(`columns.${rowIndex}.type`, val);
	}, [getValues, validateOnChange]);

	const renderDropdownCell = React.useCallback((cell: CellContext<TableRow, string>) => {
		const rowIdx = cell.row.index;
		const rowId = getRowRHFId(rowIdx);

		const row = getValues(`columns.${cell.row.index}`);
		const currentType = row?.type;

		const cols = getValues('columns') ?? [];
		const usedPresetTypes = cols.reduce<TemplateColumnFM['type'][]>((acc, column, index) => {
			if (index === rowIdx) {
				return acc; // skip current row
			}

			const type = column.type;
			// only track preset types, and keep each only once
			if (PRESET_TYPES.has(type) && !acc.includes(type)) {
				acc.push(type);
			}

			return acc;
		}, []);

		const optionsForRow = typeOptions.filter(
			(option) =>
				option.value === JobTrackingTemplateColumnType.CUSTOM ||
				option.value === currentType || // always keep current selection
				!usedPresetTypes.includes(option.value as TemplateColumnFM['type']) // hide used presets
		);

		return (
			<DropdownCellRenderer
				className={styles['dropdown-cell']}
				control={control}
				disabled={cell.row.original.type === JobTrackingTemplateColumnType.DATE}
				fieldsLength={fields.length}
				getValues={getValues}
				id={`${cell.row.index}`}
				labelKey="label"
				name={`columns.${cell.row.index}.type`}
				onChange={handleDropdownChange(cell.row.index)}
				options={optionsForRow}
				placeholder="Select Unit"
				rhfId={rowId}
				unregister={unregister}
				valueKey="value"
				withCaret={cell.row.original.type !== JobTrackingTemplateColumnType.DATE}
			/>
		);
	},
		[getRowRHFId, getValues, control, fields.length, handleDropdownChange, unregister]
	);

	const renderCheckboxCell = React.useCallback((cell: CellContext<TableRow, boolean>) => {
		return (
			<div className={styles['checkbox-cell']}>
				<CheckboxField
					control={control}
					isDisabled={cell.row.original.type === JobTrackingTemplateColumnType.DATE}
					name={`columns.${cell.row.index}.isDefinition`}
					onChange={validateOnChange}
					shouldUnregister={true}
				/>
			</div>
		);
	}, [control, validateOnChange]);

	const applyReorder = React.useCallback((reordered: TemplateColumnFM[]) => {

		const withCids = reordered.map((c) => c._cid ? c : withCidColumn(c));

		replace(withCids);

		const snapshot = getValues();
		validateColumnsArray(snapshot, setError, clearErrors, getValues);
		setShowReorder(false);
	}, [clearErrors, getValues, replace, setError]);

	// New things won't have id which is needed for form so we use _cid - we can't use index since it can be changed
	const fieldsForWrapper = React.useMemo(
		() =>
			fields.map((field) => ({
				...field,
				id: field._cid,
			})) as unknown as FieldArrayWithId<FormModel, 'columns'>[],
		[fields]
	);

	const renderDataSourcesCard = React.useCallback(
		(dataSources: ColumnDataSourceFM[], excludedIds: number[]) => {
			const latestAssociated = getLatestAssociatedDataSourcesByFamily(dataSources, excludedIds);

			if (!latestAssociated.length) {
				return null;
			}

			const firstDisplayed = latestAssociated[0];
			const meta = resolveFieldName(companyFieldReportTypes, firstDisplayed);

			if (!meta) {
				return null;
			}

			const extra = Math.max(0, latestAssociated.length - 1);

			return (
				<div className={styles['data-sources-card']}>
					{meta.reportTypeName} - {meta.reportBlockName} - <b>{meta.reportBlockFieldName}</b>
					{extra > 0 && (
						<span className={styles['data-sources-card__count']}> + {extra}</span>
					)}
				</div>
			);
		},
		[companyFieldReportTypes, getLatestAssociatedDataSourcesByFamily]
	);

	const renderDataSourcesCell = React.useCallback((cell: CellContext<TableRow, ColumnDataSourceFM>) => {
		if (cell.row.original.type !== JobTrackingTemplateColumnType.CUSTOM) {
			return (
				<div className={styles['data-sources-cell']}>{JobTrackingTemplateColumnTypeLabel[cell.row.original.type]}</div>
			);
		}

		const dataSources = cell.row.original.dataSources;
		const excludedIds = cell.row.original.excludedFieldReportIds ?? [];
		const dataSourcesCard = renderDataSourcesCard(dataSources, excludedIds);
		const hasAnyFields = !!dataSourcesCard;

		return (
			<div className={styles['data-sources-cell']}>
				{renderDataSourcesCard(dataSources, excludedIds)}
				<Button
					icon="ActionPlusSmall"
					label={hasAnyFields ? 'Edit Fields' : 'Add Fields'}
					onClick={openModal(cell.row.index)}
					style="link"
				/>
			</div>
		);
	}, [openModal, renderDataSourcesCard]);

	const renderDefinitionColumnHeader = React.useCallback(() => {
		const tooltipMessage = `Definition columns are used to group data with them as a factor. 
		All data from field reports with same value in definition column will be grouped in a single row. 
		Multiple columns can be defined as definition ones, allowing for finer granulation`;

		return <div className={styles['tooltip-header']}> <Tooltip message={tooltipMessage}>Definition <span className="icon-info" /></Tooltip></div>;
	}, []);

	const renderDataColumnHeader = React.useCallback(() => {
		const tooltipMessage = `Decide which data gets used in a certain job tracking column. 
		Custom columns can use maximum of one field per report type. 
		Default and Preset ones cannot be configured`;

		return <div className={styles['tooltip-header']}><Tooltip message={tooltipMessage}>Data from Fields <span className="icon-info" /></Tooltip></div >;
	}, []);

	const columns: SimpleTableProps<TableRow>['columns'] = React.useMemo(() => [
		{
			id: 'index',
			accessor: 'index',
			header: 'Position',
			cell: renderIndexCell,
			enableSorting: false,
			size: '1fr',
		},
		{
			id: 'type',
			accessor: 'type',
			header: 'Column Type',
			cell: renderDropdownCell,
			enableSorting: false,
			size: '2fr',
		},
		{
			id: 'name',
			accessor: 'name',
			header: 'Column Name *',
			cell: renderInputCell('name', 'text'),
			enableSorting: false,
			size: '2fr',
		},
		{
			id: 'isDefinition',
			accessor: 'isDefinition',
			header: renderDefinitionColumnHeader,
			cell: renderCheckboxCell,
			enableSorting: false,
			size: '1fr',
		},
		{
			id: 'dataSources',
			accessor: 'dataSources',
			header: renderDataColumnHeader,
			cell: renderDataSourcesCell,
			enableSorting: false,
			size: '6fr',
		},
	], [renderCheckboxCell, renderDataColumnHeader, renderDataSourcesCell, renderDefinitionColumnHeader, renderDropdownCell, renderIndexCell, renderInputCell]);

	const onClickSave = React.useCallback(
		() => void handleSubmit(onSubmitRHF)(),
		[handleSubmit, onSubmitRHF]
	);

	const onBack = React.useCallback(() => {
		navigate(-1);
	}, [navigate]);

	const showReorderColumnsModal = React.useCallback(() => {
		setShowReorder(true);
	}, []);

	const closeReorderColumnsModal = React.useCallback(() => {
		setShowReorder(false);
	}, []);

	const checkIsRowDeletable = React.useCallback((row: Omit<TemplateColumnFM, 'id'> & SimpleTableRow) => {
		return row.type !== JobTrackingTemplateColumnType.DATE;
	}, []);

	const onNameFieldChangeBlur = React.useCallback(() => {
		void trigger(['name']);
	}, [trigger]);

	const onDescriptionFieldChangeBlur = React.useCallback(() => {
		void trigger(['description']);
	}, [trigger]);

	const title = React.useMemo(() => {
		const name = (templateName ?? '').trim();
		return name ? name : 'New Job Tracking Template';
	}, [templateName]);

	const renderPreviewCell = React.useCallback((
		cell: CellContext<PreviewRow & SimpleTableRow, string>
	) => {
		const value = cell.getValue();

		return (
			<div className={styles['preview-cell']}>
				{value || 'N/A'}
			</div>
		);
	}, []);

	const previewColumns: BasicTableProps<PreviewRow & SimpleTableRow>['columns'] = React.useMemo(() => {
		const cols = watchColumns ?? [];

		return cols.reduce<BasicTableProps<PreviewRow & SimpleTableRow>['columns']>((acc, col, index) => {
			if (!col) {
				return acc;
			}

			const id = (col as TemplateColumnFM)._cid ?? `col_${index}`;
			const headerLabel = (col as TemplateColumnFM).name?.trim()
				|| JobTrackingTemplateColumnTypeLabel[(col as TemplateColumnFM).type]
				|| `Column ${index + 1}`;

			acc.push({
				id,
				accessor: id,
				header: headerLabel,
				cell: renderPreviewCell,
				enableSorting: false,
			});

			return acc;
		}, [{
			id: 'columnTitle',
			accessor: 'columnTitle',
			header: 'COLUMN TITLE:',
			cell: renderPreviewCell,
			enableSorting: false,
		}]);
	}, [watchColumns, renderPreviewCell]);

	const previewRows: Array<PreviewRow & SimpleTableRow> = React.useMemo(() => {
		const cols = watchColumns ?? [];

		if (!cols.length) {
			return [];
		}

		const row: PreviewRow & SimpleTableRow = { index: 0 };

		cols.forEach((col, index) => {
			if (!col) {
				return;
			}

			const column = col as TemplateColumnFM;
			const id = column._cid ?? `col_${index}`;

			if (column.type !== JobTrackingTemplateColumnType.CUSTOM) {
				row[id] = JobTrackingTemplateColumnTypeLabel[column.type] ?? '';
				return;
			}

			const dataSources = column.dataSources ?? [];
			const excludedIds = column.excludedFieldReportIds ?? [];

			if (!dataSources.length || !companyFieldReportTypes) {
				row[id] = '';
				return;
			}

			const latestAssociated = getLatestAssociatedDataSourcesByFamily(
				dataSources,
				excludedIds
			);

			const labels = latestAssociated.reduce<string[]>((acc, dataSource) => {
				const meta = resolveFieldName(companyFieldReportTypes, dataSource);

				if (!meta) {
					return acc;
				}

				acc.push(
					`${meta.reportTypeName} - ${meta.reportBlockName} - ${meta.reportBlockFieldName}`
				);

				return acc;
			}, []);

			row[id] = labels.join(', ');
		});

		return [{ ...row, columnTitle: 'DATA FROM:' }];
	}, [
		watchColumns,
		companyFieldReportTypes,
		getLatestAssociatedDataSourcesByFamily,
	]);

	const warningModalBody = React.useMemo(() => {
		const jobs = jobsForTemplate?.jobs ?? [];

		return (
			<div>
				{jobs.length > 0 ?
					<>
						<div className={styles['info-margin']}>
							The following jobs use this template and will be updated:
						</div>
						<div>
							{jobs.map((job) => (
								<div key={job.id}>
									<b>{job.jobCode}</b>
								</div>
							))}
						</div>
					</> :
					<div>There are no jobs associated with this Job Tracking Template.</div>
				}
			</div>
		);
	}, [jobsForTemplate]);

	return (
		<div>
			<form onSubmit={handleSubmit(onSubmitRHF)}>
				<div className={styles.container}>
					<Row className={styles.section}>
						<Col md={10}>
							<div className={styles.title}>{title}</div>
						</Col>
						<Col md={14}>
							<div className={styles['buttons-container']}>
								<Button
									label="Back"
									onClick={onBack}
									style="secondary"
								/>
								<Button
									disabled={hasAnyErrors || hasOutdatedColumns}
									label="Save"
									onClick={onClickSave}
									style="primary"
								/>
								<ConfirmationModal
									body={warningModalBody}
									closeModal={closeWarningModal}
									confirmAction={confirmWarningModal}
									confirmText="Understood"
									hideOnConfirm={false}
									modalStyle="warning"
									showCancel={false}
									showModal={warningModalOpen}
									size="md"
									title="Job Tracking Table Updated"
								/>
							</div>
						</Col>
					</Row>
					<Row className={styles.section}>
						<div className="m-b-m">
							<SegmentLabel
								label="Template Details"
								tooltip="Job Tracking Template creates columns that display data from fields in Report Types. These fields can then be used to track data over time for a job."
							/>
						</div>
						<Col md={10}>
							<InputField<FormModel>
								control={control}
								defaultValue={initial?.name ?? ''}
								label="Template Name *"
								name="name"
								onBlur={onNameFieldChangeBlur}
								onChange={onNameFieldChangeBlur}
								placeholder="Enter Template Name"
								rhfId="template-name"
								rules={{ required: 'Name is required', maxLength: 255 }}
								type={'text'}
								unregister={unregister}
							/>
						</Col>
						<Col md={10}>
							<InputField<FormModel>
								control={control}
								defaultValue={initial?.description ?? ''}
								label="Description"
								name="description"
								onBlur={onDescriptionFieldChangeBlur}
								onChange={onDescriptionFieldChangeBlur}
								placeholder="Enter Description"
								rhfId="template-description"
								type={'text'}
								unregister={unregister}
							/>
						</Col>
					</Row>
					<Row className={styles.section}>
						<Col md={24}>
							<div className={styles['section-header']}>
								<SegmentLabel
									label="Columns"
									tooltip="A configurable list of columns that define the structure of this tracking template. Each column can be set as a preset, default, or custom type, with optional field mappings and definition flags to control data display."
								/>
								<div className={styles['section-header']}>
									<Button
										icon="ActionPlusSmall"
										label="Add New Column"
										onClick={addNewColumn}
										style="link"
									/>
									<Button
										icon="FilterSort"
										label="Rearrange Columns"
										onClick={showReorderColumnsModal}
										style="link"
									/>
								</div>
							</div>
							<SimpleTableRHFWrapper
								canDeleteRow={checkIsRowDeletable}
								columns={columns}
								fields={fieldsForWrapper}
								footerButtonsLeft={footerButtonsLeft}
								formAccessorName="columns"
								ref={tableRef}
								remove={guardedRemove}
							/>
						</Col>
					</Row>
					<Row className={styles.section}>
						<Col md={24}>
							<SimpleTable
								columns={previewColumns}
								key={previewColumns.map((c) => c.id).join('|')} // real important since columns are dynamic
								label="Table Template Preview"
								labelTooltip="A read-only preview of the tracking table as it will appear based on the current column configuration, displaying column titles and their associated data sources."
								rows={previewRows}
							/>
						</Col>
					</Row>
				</div>
			</form>
			<AddFieldsToColumnsModal
				baselineColumn={modalBaseline}
				closeModal={closeModal}
				companyFieldReportTypes={companyFieldReportTypes}
				initialState={modalInitial}
				isEdit={props.isEdit}
				save={saveColumnFieldChanges}
				showModal={showModal}
			/>
			<ReorderColumnsModal
				columns={getValues('columns') ?? []}
				onClose={closeReorderColumnsModal}
				onSave={applyReorder}
				show={showReorder}
			/>
		</div>
	);
};

function mapDispatchToProps() {
	return {
		getCompanyFieldReportTypes: FieldReportActions.findAllReportTypesForCompany,
		getJobsForTemplate: JobTrackingActions.findJobsForTemplate,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(JobTrackingForm);
